import { Component, inject, OnInit } from '@angular/core';
import { ReserveLocationService } from '../../shared/services/reserve-location.service';
import {
  CheckLocationRequest,
  OrgDetail,
} from '../../shared/interfaces/reserve-location.model';
import { API_PARAMS } from '../../shared/constants/api.constants';
import { FormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { SharedStorageService } from '../../shared/services/shared-storage.service';
import { UserPlanRequest } from '../../shared/interfaces/reserve-health.model';
import { Router } from '@angular/router';
import { ID_LOGIN_TYPE } from '../../shared/constants/common.constants';
import { NgOptimizedImage } from '@angular/common';
import { ConfigLoaderService } from '../../shared/services/config-loader.service';
import { BusinessPlanRequest } from '../../shared/interfaces/reserve-loading.model';
import { AuthService } from '../../shared/services/auth.service';
import { IdLoginComponent } from '../id-login/id-login.component';

/**
 * 選擇預約健檢檢查地點
 */
@Component({
  selector: 'app-reserve-location',
  standalone: true,
  imports: [FormsModule, MatButton, NgOptimizedImage],
  templateUrl: './reserve-location.component.html',
  styleUrl: './reserve-location.component.scss',
})
export class ReserveLocationComponent implements OnInit {
  reserveLocationService: ReserveLocationService = inject(
    ReserveLocationService
  );
  sharedStorageService: SharedStorageService = inject(SharedStorageService);
  configLoaderService: ConfigLoaderService = inject(ConfigLoaderService);
  router: Router = inject(Router);
  authService: AuthService = inject(AuthService);
  locations?: Array<OrgDetail>; // 所有預約地點信息
  selectedLocation?: string; // 選擇的預約地點

  ngOnInit() {
    // 設置當前預約步驟為1
    setTimeout(() => {
      this.sharedStorageService.set('reserveStep', 1);
    });

    const reserveType =
      this.sharedStorageService.get('reserveType') ?? 'personal';
    const request: CheckLocationRequest = {
      reservationType: reserveType === ID_LOGIN_TYPE.PERSONAL ? '1' : '2',
      companyNo:
        reserveType === ID_LOGIN_TYPE.ENTERPRISE
          ? (this.sharedStorageService.get('businessPlanRequest')?.companyNo ?? '')
          : '',
    };

    // 查詢健檢中心
    this.reserveLocationService.checkLocations(request).subscribe(res => {
      if (res.success && res.data?.orgDetail) {
        let filteredLocations = res.data.orgDetail;

        // 檢查環境配置，只有 prod 環境才過濾台南據點
        const env = (window as any)?.env;
        const isProdEnvironment = env?.environment === 'prod';

        // 只有 prod 環境，且是個人預約才只顯示台南據點
        if (isProdEnvironment && reserveType === ID_LOGIN_TYPE.PERSONAL) {
          filteredLocations = filteredLocations.filter(org => org.orgNo === '05'); // 只保留台南據點
        }
        
        this.locations = filteredLocations.sort((a, b) => {
          return a.orgNo.localeCompare(b.orgNo);
        });
      } else {
        // EC2004 錯誤狀況，直接設為空陣列
        this.locations = [];
        console.warn('查詢據點失敗:', res.statusCode, res.errorMessage);
      }
    });

    if (this.sharedStorageService.get('reserveType') === '2') {
      let businessPlanRequest;
      if (this.sharedStorageService.has('businessPlanRequest')) {
        businessPlanRequest = this.sharedStorageService.get('businessPlanRequest')!;
        this.selectedLocation = businessPlanRequest.orgNo;
        this.sharedStorageService.set('businessPlanRequest', businessPlanRequest);
        let userPlanRequest = this.sharedStorageService.get('userPlanRequest');
        if (userPlanRequest) {
          userPlanRequest.orgNo = businessPlanRequest.orgNo;

          // 企業預約時清空自覺症狀
          userPlanRequest.subjectiveSymptoms = [];

          this.sharedStorageService.set('userPlanRequest', userPlanRequest);
        }
      }
    } else {
    let userPlanRequest;
    if (this.sharedStorageService.has('userPlanRequest')) {
      userPlanRequest = this.sharedStorageService.get('userPlanRequest')!;
      this.selectedLocation = userPlanRequest.orgNo;
    } else {
      // 會員登入時，將createMemberRequest的資料帶入userPlanRequest
      if (this.sharedStorageService.get('loginStatus') === 'memberLogin' && 
      this.sharedStorageService.get('createMemberRequest')?.idNo &&
      this.authService.isLogin()) {
        userPlanRequest = {
          orgNo: '',
          gender: this.sharedStorageService.get('createMemberRequest')?.gender ?? '',
          birthday: this.sharedStorageService.get('createMemberRequest')?.birthday ?? '',
          age: '',
          idNo: this.sharedStorageService.get('createMemberRequest')?.idNo ?? '',
          uuid: '',
          name: this.sharedStorageService.get('createMemberRequest')?.name ?? '',
          cell: this.sharedStorageService.get('createMemberRequest')?.cell ?? '',
          email: this.sharedStorageService.get('createMemberRequest')?.email ?? '',
          telH: '',
          telO: '',
          zipCode: '',
          history: [],
          family: [],
          subjectiveSymptoms: [],
        }
        this.sharedStorageService.set('userPlanRequest', userPlanRequest);
      } else {
        const idNo = this.sharedStorageService.get('idLoginRequest')?.idNo ?? '';
        userPlanRequest = {
          orgNo: '',
          gender: '',
          birthday: '',
          age: '',
          idNo: idNo ?? '',
          uuid: '',
          name: '',
          cell: '',
          email: '',
          telH: '',
          telO: '',
          zipCode: '',
          history: [],
          family: [],
          subjectiveSymptoms: [],
        };
        this.sharedStorageService.set('userPlanRequest', userPlanRequest);
      }
    }
    }
  }

  // 選擇預約地點
  chooseLocation(orgNo: string) {
    if (this.sharedStorageService.get('reserveType') === '2') {
      const businessPlanRequest: BusinessPlanRequest = this.sharedStorageService.get('businessPlanRequest')!;
      const userPlanRequest: UserPlanRequest = this.sharedStorageService.get('userPlanRequest')!;
      businessPlanRequest['orgNo'] = orgNo;
      userPlanRequest['orgNo'] = orgNo;
      this.sharedStorageService.set('userPlanRequest', userPlanRequest);
      this.sharedStorageService.set('businessPlanRequest', businessPlanRequest);
      this.router.navigate(['/healthcheck/reserve/personalinfo']).then();
    } else {
      const userPlanRequest: UserPlanRequest =
      this.sharedStorageService.get('userPlanRequest')!;
    userPlanRequest['orgNo'] = orgNo;
    this.sharedStorageService.set('userPlanRequest', userPlanRequest);
    this.router.navigate(['/healthcheck/reserve/personalinfo']).then();
    }
  }
}
